import { useSelector } from "react-redux";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import { UpdateRouteRateUIRequest } from "./UpdateRouteRateModal";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircleOutline";
import { fetchRegionNameListForFuelSurcharge } from "../../../../app/features/ratecard/ratecardSlice";
import getAxiosError from "../../../../app/api/axiosError";
import {
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
} from "@mui/material";
import { Field, FieldArray, FieldArrayRenderProps, FormikTouched } from "formik";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import { FUEL_SURCHARGE_FIELD_TYPES } from "../RateCardConstants";
import { UpdateFuelSurchargeFields } from "../dtos/UpdateRateCardRequest";
import { useSubmitModal } from "../../../../components/common/modalPopup/useSubmitModal";
import FileUploadModal from "../../../../components/common/modalPopup/FileUploadModal";
import { prepareAndDownloadFile } from "../../../helper/helper";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import Loader from "../../../../components/common/loader/Loader";
import { mapSnakeCaseToCapitalCaseWithSpaces } from "../../../../components/common/utils/StringUtils";

interface Props {
  values: UpdateRouteRateUIRequest;
  touched: FormikTouched<UpdateRouteRateUIRequest>;
  handleChange: any;
  handleBlur: any;
  setFieldValue: any;
}

const FuelSurcharge = (props: Props) => {
  const { values, handleChange, handleBlur, setFieldValue } = props;

  console.log("Values", values);

  const ArrayHelperRef = useRef<FieldArrayRenderProps>();

  // import store data from "Redux-Store"
  const { isLoading, regionNameList } = useSelector(
    (state: RootState) => state.ratecard
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (values.show_fuel_surcharge && values.fuelType) {
      fetchRegionNameListForFuelSurchargeData();

      if (props.touched.fuelType) {
        // this check is here else regionName will be changed to undefined on the first render as well
        // so the touched map from formik tells us if fuelType has been deliberately changed after the intial render is done
        setFieldValue("regionName", undefined);
      }
    }
  }, [values.show_fuel_surcharge, values.fuelType]);

  // get overview data of store
  const fetchRegionNameListForFuelSurchargeData = async () => {
    try {
      console.log(`In ratecard fuelType = ${values.fuelType}`);

      const response = await dispatch(
        fetchRegionNameListForFuelSurcharge({
          fuelType: values.fuelType ?? "petrol",
        })
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "fetchRegionNameListForFuelSurcharge",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fetchRegionNameListForFuelSurcharge",
      });
    } finally {
      // setDataFetchig(false);
    }
  };

  const isDisabledField = (
    fuelSurchargeFields: UpdateFuelSurchargeFields[],
    fieldToCheck: string
  ) => {
    let fieldsStringList: string[] = [];
    fuelSurchargeFields.forEach((field) =>
      fieldsStringList.push(field.fieldName)
    );

    return fieldsStringList.includes(fieldToCheck);
  };

  const handleFSCFileData = async (
    fileMappedData: any[],
    invalidData: any[]
  ) => {
    // flag file upload process as true
    // used for validation
    values.lookup_file_upload = true;
    const errorFileName = "Error_FSC_Table_";

    try {
      const invalidFileData = invalidData.map(({ data, error }) => ({
        AtLeast: data.rate_range_start,
        "But Less Than": data.rate_range_end,
        Surcharge: data.fuel_surcharge_percentage,
        Error: error,
      }));
      // if (fileMappedData.length === 0) {
      if (invalidFileData.length > 0) {
        // reset the lookup data
        // for validation
        values.fuelSurchargeLookup = [];
        prepareAndDownloadFile(invalidFileData, errorFileName);
        return false;
      } else {
        values.fuelSurchargeLookup = fileMappedData;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "FSCTableException",
      });
    }
    return true;
  };

  // open  modal logic  incase user selects any of the store
  const [fileUploadModal, openFileUploadModal] = useSubmitModal(
    (closeFileUploadModal) => (
      <FileUploadModal
        closeFileUploadModal={closeFileUploadModal}
        onCompleted={() => {
          // implement file upload modal onCompleted
        }}
        expectedColumns={[
          {
            name: "At Least",
            mandatory: true,
            type: "number",
            camelCaseName: "rateRangeStart",
            regex: /^\d*\.?\d*$/,
          },
          {
            name: "But Less Than",
            mandatory: true,
            type: "number",
            camelCaseName: "rateRangeEnd",
            regex: /^\d*\.?\d*$/,
          },
          {
            name: "Surcharge",
            mandatory: true,
            type: "percentage",
            camelCaseName: "fuelSurchargePercentage",
            regex: /^\d*\.?\d*$/,
          },
        ]}
        asyncSubmitOperation={handleFSCFileData}
        sampleFileName={"FSC-Table-Sample.xlsx"}
        acceptNumberAsZero={true}
        acceptPercentageAsZero={true}
      />
    )
  );

  return (
    <>
      <Grid container alignItems={"center"}>
        <Loader isLoading={isLoading} />
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            id="show_fuel_surcharge"
            name="show_fuel_surcharge"
            label="Fuel Surcharge"
            labelPlacement="start"
            sx={{ mt: 2 }}
            checked={values.show_fuel_surcharge}
            control={
              <Field
                id="show_fuel_surcharge"
                name="show_fuel_surcharge"
                component={Switch}
                type="checkbox"
               // size="small"
                onChange={handleChange}
                onBlur={handleBlur}
              />
            }
          />
        </Grid>
        <Grid
          container
          item
          xs={12}
          sm={8}
          columnSpacing={2}
          alignItems={"center"}
          display={values.show_fuel_surcharge ? "" : "none"}
        >
          <Grid item xs={12} sm={4} paddingTop={1}>
            <RadioGroup
              row
              id="fuelType"
              name="fuelType"
              value={values.fuelType}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <FormControlLabel
                value="petrol"
                control={<Radio />}
                label="Petrol"
              />
              <FormControlLabel
                value="diesel"
                control={<Radio />}
                label="Diesel"
              />
            </RadioGroup>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormikTextField
              select
              margin="normal"
              id="regionName"
              name="regionName"
              label="Choose city/state"
              isDisabled={
                !values.fuelType || values.fuelType === "" ? true : false
              }
              size="small"
              fullWidth
              variant="outlined"
            >
              {regionNameList.map((region, index) => (
                <MenuItem key={index} value={region.toLowerCase()}>
                  {region}
                </MenuItem>
              ))}
            </FormikTextField>
          </Grid>
          <Grid item xs={12} sm={4} paddingTop={1}>
            <Button
              id="fscFile"
              name="fscFile"
              variant="contained"
              size="medium"
              onClick={openFileUploadModal}
              disabled={
                !values.fuelType ||
                values.fuelType === "" ||
                !values.regionName ||
                values.regionName === ""
                  ? true
                  : false
              }
              color={
                // values.lookup_file_upload &&
                values.fuelSurchargeLookup.length === 0 ? "error" : "success"
              }
              endIcon={
                // values.lookup_file_upload &&
                values.fuelSurchargeLookup.length === 0 ? (
                  <ClearIcon />
                ) : (
                  <CheckIcon />
                )
              }
            >
              Upload FSC table
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {/* Fuel Surcharge Fields */}
      <Grid
        container
        item
        alignItems={"center"}
        columnSpacing={2}
        display={values.show_fuel_surcharge ? "" : "none"}
      >
        <FieldArray
          name="fuelSurchargeFields"
          render={(arrayHelpers) => {
            ArrayHelperRef.current = arrayHelpers;

            return values.fuelSurchargeFields.map((field, index) => (
              <>
                <Grid item sm={1} display={index === 0 ? "none" : ""}>
                  <FormikTextField
                    select
                    margin="normal"
                    id={`fuelSurchargeFields.${index}.fieldOperator`}
                    name={`fuelSurchargeFields.${index}.fieldOperator`}
                    label="Plus/Minus"
                    size="small"
                    fullWidth
                    variant="outlined"
                  >
                    {["+", "-"].map((fieldOperator, index) => (
                      <MenuItem key={index} value={fieldOperator}>
                        {fieldOperator}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item sm={index === 0 ? 3 : 2}>
                  <FormikTextField
                    select
                    margin="normal"
                    id={`fuelSurchargeFields.${index}.fieldName`}
                    name={`fuelSurchargeFields.${index}.fieldName`}
                    label="Field Name"
                    size="small"
                    fullWidth
                    variant="outlined"
                  >
                    {FUEL_SURCHARGE_FIELD_TYPES.map((field, index) => (
                      <MenuItem
                        key={index}
                        value={field}
                        disabled={isDisabledField(
                          values.fuelSurchargeFields,
                          field
                        )}
                      >
                        {mapSnakeCaseToCapitalCaseWithSpaces(field)}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Grid>
                <Grid item sm={1}>
                  <IconButton
                    color="error"
                    onClick={() => {
                      arrayHelpers.remove(index);
                    }}
                  >
                    <RemoveCircleIcon />
                  </IconButton>
                </Grid>
              </>
            ));
          }}
        />
      </Grid>
      <Grid container item display={values.show_fuel_surcharge ? "" : "none"}>
        <Button
          variant="contained"
          onClick={() => {
            if (values.fuelSurchargeFields.length === 0)
              ArrayHelperRef.current?.push({
                fieldOperator: "+",
                fieldName: undefined,
              });
            else ArrayHelperRef.current?.push({});
          }}
        >
          Add Fields
        </Button>
      </Grid>
      {fileUploadModal}
    </>
  );
};

export default FuelSurcharge;
